/**
 * Text Component Variants
 *
 * CVA variants for the Text component.
 * Separated to allow React Fast Refresh to work properly.
 */

import {cva} from 'class-variance-authority';
import type {ResponsiveTextSize, TextSize} from './typography.types';

// =============================================================================
// TEXT VARIANTS (CVA)
// =============================================================================

/**
 * Text style variants using class-variance-authority.
 *
 * Size classes map to typography tokens:
 * - Responsive: text-body-xl, text-body-lg, text-body-responsive, text-body-sm-responsive
 * - Fixed: text-label, text-caption, text-xs
 */
export const textVariants = cva(
  // Base styles - none, let variants handle everything
  '',
  {
    variants: {
      size: {
        xl: 'text-body-xl', // 20px → 24px (responsive)
        lg: 'text-body-lg', // 18px → 20px (responsive)
        base: 'text-body-responsive', // 16px → 18px (responsive)
        sm: 'text-body-sm-responsive', // 14px → 16px (responsive)
        xs: 'text-xs', // 12px (fixed)
        label: 'text-label', // 14px (fixed, designed for labels)
        caption: 'text-caption', // 12px (fixed, designed for captions)
        // Body sizes (responsive)
        'body-xl': 'text-body-xl',
        'body-lg': 'text-body-lg',
        'body-responsive': 'text-body-responsive',
        'body-sm-responsive': 'text-body-sm-responsive',
        // Heading sizes (for larger text)
        'heading-1': 'text-heading-1',
        'heading-2': 'text-heading-2',
        'heading-3': 'text-heading-3',
        'heading-4': 'text-heading-4',
        // Display sizes (for hero text)
        'display-1': 'text-display-1',
        'display-2': 'text-display-2',
        'display-3': 'text-display-3',
      },
      variant: {
        default: 'text-foreground',
        muted: 'text-muted-foreground',
        strong: 'text-foreground font-semibold',
        primary: 'text-primary',
        secondary: 'text-secondary',
        accent: 'text-accent',
        success: 'text-success',
        warning: 'text-warning',
        error: 'text-error',
      },
      weight: {
        normal: 'font-normal',
        medium: 'font-medium',
        semibold: 'font-semibold',
        bold: 'font-bold',
      },
      align: {
        left: 'text-left',
        center: 'text-center',
        right: 'text-right',
      },
      truncate: {
        true: 'truncate',
        false: '',
      },
      italic: {
        true: 'italic',
        false: '',
      },
      underline: {
        true: 'underline underline-offset-2',
        false: '',
      },
      leadingTrim: {
        true: 'leading-none',
        false: '',
      },
    },
    defaultVariants: {
      size: 'base',
      variant: 'default',
      align: 'left',
      truncate: false,
      italic: false,
      underline: false,
      leadingTrim: false,
    },
  }
);

/**
 * Line clamp classes
 */
export const lineClampClasses: Record<1 | 2 | 3 | 4 | 5 | 6, string> = {
  1: 'line-clamp-1',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
  5: 'line-clamp-5',
  6: 'line-clamp-6',
};

/**
 * Size to CSS class mapping
 */
const SIZE_TO_CLASS: Record<TextSize, string> = {
  xl: 'text-body-xl',
  lg: 'text-body-lg',
  base: 'text-body-responsive',
  sm: 'text-body-sm-responsive',
  xs: 'text-xs',
  label: 'text-label',
  caption: 'text-caption',
  'body-xl': 'text-body-xl',
  'body-lg': 'text-body-lg',
  'body-responsive': 'text-body-responsive',
  'body-sm-responsive': 'text-body-sm-responsive',
  'heading-1': 'text-heading-1',
  'heading-2': 'text-heading-2',
  'heading-3': 'text-heading-3',
  'heading-4': 'text-heading-4',
  'display-1': 'text-display-1',
  'display-2': 'text-display-2',
  'display-3': 'text-display-3',
};

/**
 * Text variants without size default - used for responsive sizes
 * to avoid applying the default 'base' size when size is handled separately
 */
export const textVariantsWithoutSize = cva(
  '',
  {
    variants: {
      variant: {
        default: 'text-foreground',
        muted: 'text-muted-foreground',
        strong: 'text-foreground font-semibold',
        primary: 'text-primary',
        secondary: 'text-secondary',
        accent: 'text-accent',
        success: 'text-success',
        warning: 'text-warning',
        error: 'text-error',
      },
      weight: {
        normal: 'font-normal',
        medium: 'font-medium',
        semibold: 'font-semibold',
        bold: 'font-bold',
      },
      align: {
        left: 'text-left',
        center: 'text-center',
        right: 'text-right',
      },
      truncate: {
        true: 'truncate',
        false: '',
      },
      italic: {
        true: 'italic',
        false: '',
      },
      underline: {
        true: 'underline underline-offset-2',
        false: '',
      },
      leadingTrim: {
        true: 'leading-none',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      align: 'left',
      truncate: false,
      italic: false,
      underline: false,
      leadingTrim: false,
    },
  }
);

/**
 * Build responsive text size classes from a responsive size object.
 * Converts { base: 'body-sm-responsive', sm: 'body-lg' } to 'text-body-sm-responsive sm:text-body-lg'
 */
export function buildResponsiveTextSizeClasses(size: ResponsiveTextSize): string {
  const classes: string[] = [];

  if (size.base) {
    const baseClass = SIZE_TO_CLASS[size.base];
    if (baseClass) classes.push(baseClass);
  }
  if (size.sm) {
    const smClass = SIZE_TO_CLASS[size.sm];
    if (smClass) classes.push(`sm:${smClass}`);
  }
  if (size.md) {
    const mdClass = SIZE_TO_CLASS[size.md];
    if (mdClass) classes.push(`md:${mdClass}`);
  }
  if (size.lg) {
    const lgClass = SIZE_TO_CLASS[size.lg];
    if (lgClass) classes.push(`lg:${lgClass}`);
  }
  if (size.xl) {
    const xlClass = SIZE_TO_CLASS[size.xl];
    if (xlClass) classes.push(`xl:${xlClass}`);
  }

  return classes.join(' ');
}
